import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ServiceLike {
  service_code?: string;
  service_name?: string;
  time_range?: string;
}

interface CapacityViewSwitchProps {
  /** All service slices visible across the current data set; used to derive the pill list. */
  services: ServiceLike[];
  /** Current selected view: "PEAK" or a service code. */
  value: string;
  /** Called with the new view when a pill is selected. */
  onChange: (view: string) => void;
  /** Resolves a service code to its localized display name. */
  resolveServiceName: (code: string, fallback?: string) => string;
  /** When true, only Peak is active; non-Peak buttons are disabled. */
  disabled?: boolean;
}

const MAX_VISIBLE_PINNED = 2;

function parseMinutes(hhmm?: string): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/**
 * Service view-switch — Peak | up-to-2 pinned services | overflow dropdown.
 * Pinned slot count shrinks dynamically (2→1→0) if the container is too
 * narrow to fit the full row (e.g. long German labels in a small card).
 * Pinned selection = longest duration first, ties broken by earliest start;
 * pinned services render in start-time order.
 *
 * Shared between the Dashboard Group Overview and the Capacity Overview screen,
 * with persistence handled by useCapacityViewPreference.
 */
export function CapacityViewSwitch({
  services,
  value,
  onChange,
  resolveServiceName,
  disabled = false,
}: CapacityViewSwitchProps) {
  const { t } = useLingui();
  const availableServices = useMemo(() => {
    const seen = new Map<
      string,
      { code: string; name: string; start: number; duration: number }
    >();
    for (const s of services) {
      if (!s.service_code) continue;
      if (s.service_code.toUpperCase() === 'FULL_DAY') continue;
      if (seen.has(s.service_code)) continue;
      const [from, to] = (s.time_range ?? '').split('-');
      const start = parseMinutes(from) ?? Number.MAX_SAFE_INTEGER;
      const end = parseMinutes(to);
      const duration =
        end !== null && start !== Number.MAX_SAFE_INTEGER ? end - start : 0;
      seen.set(s.service_code, {
        code: s.service_code,
        name: resolveServiceName(s.service_code, s.service_name),
        start,
        duration,
      });
    }
    return Array.from(seen.values());
  }, [services, resolveServiceName]);

  // visibleCount is the number of pinned pills we are *currently* trying to
  // render. Starts at the upper bound; the layout effect below shrinks it to
  // fit the container width. Reset to the bound whenever inputs change.
  const [visibleCount, setVisibleCount] = useState(MAX_VISIBLE_PINNED);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    setVisibleCount(MAX_VISIBLE_PINNED);
  }, [containerWidth, availableServices, value]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth + 1 && visibleCount > 0) {
      setVisibleCount((c) => c - 1);
    }
  }, [visibleCount, containerWidth, availableServices, value]);

  const pinnedServices = useMemo(() => {
    const byDuration = [...availableServices].sort(
      (a, b) => b.duration - a.duration || a.start - b.start,
    );
    const top = byDuration.slice(0, visibleCount);
    return [...top].sort((a, b) => a.start - b.start);
  }, [availableServices, visibleCount]);

  const overflowServices = useMemo(() => {
    const pinnedCodes = new Set(pinnedServices.map((s) => s.code));
    return availableServices
      .filter((s) => !pinnedCodes.has(s.code))
      .sort((a, b) => a.start - b.start);
  }, [availableServices, pinnedServices]);

  if (availableServices.length === 0) return null;

  // Mirror the Dashboard pattern: when the active selection is in overflow,
  // swap it into the last pinned slot so the user's current choice always reads
  // as a pill.
  let pinned = pinnedServices;
  let overflow = overflowServices;
  const activeInOverflow = overflowServices.find((s) => s.code === value);
  if (activeInOverflow && pinnedServices.length > 0) {
    const displaced = pinnedServices[pinnedServices.length - 1];
    pinned = [...pinnedServices.slice(0, -1), activeInOverflow].sort(
      (a, b) => a.start - b.start,
    );
    overflow = [
      displaced,
      ...overflowServices.filter((s) => s.code !== activeInOverflow.code),
    ].sort((a, b) => a.start - b.start);
  }

  return (
    <div
      ref={containerRef}
      className="flex gap-1 p-1 bg-muted rounded-md overflow-hidden max-w-full min-w-0"
    >
      <Button
        size="sm"
        variant={value === 'PEAK' ? 'default' : 'ghost'}
        onClick={() => onChange('PEAK')}
      >
        <Trans id="dashboard.groups.view.peak">Peak</Trans>
      </Button>
      {pinned.map((s) => (
        <Button
          key={s.code}
          size="sm"
          variant={value === s.code ? 'default' : 'ghost'}
          disabled={disabled}
          onClick={() => onChange(s.code)}
        >
          {s.name}
        </Button>
      ))}
      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              aria-label={t({ id: 'capacity.viewSwitch.moreServices', message: 'More services' })}
              disabled={disabled}
            >
              <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflow.map((s) => (
              <DropdownMenuItem
                key={s.code}
                onSelect={() => onChange(s.code)}
              >
                {value === s.code ? '✓ ' : ''}
                {s.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
